import React from 'react';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { UserCircle2 } from 'lucide-react';

/**
 * Sessão 32 — QW1: Customer allocation badge.
 *
 * Surfaces "this SKU has X units already allocated for customer(s)" so
 * the operator never accidentally orders without seeing the commercial context.
 *
 * Per AI_ROLE_CHARTER.md §6 (AP-AI-9): never suggest based on forecast alone —
 * customer allocations are first-class signal.
 *
 * Props:
 *   allocations: [{ source: 'po'|'cart', reference, quantity, client_reference, status }]
 *   totalQty: number
 *   testid: optional test id suffix
 */
export const CustomerAllocationBadge = ({ allocations = [], totalQty = 0, testid }) => {
  if (!allocations.length || totalQty <= 0) return null;

  // Group by client_reference for cleaner display
  const byClient = allocations.reduce((acc, a) => {
    const key = a.client_reference || 'Cliente sem referência';
    if (!acc[key]) acc[key] = { qty: 0, sources: [] };
    acc[key].qty += a.quantity || 0;
    acc[key].sources.push(a);
    return acc;
  }, {});

  const clientCount = Object.keys(byClient).length;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className="bg-indigo-100 text-indigo-700 border-indigo-200 h-6 px-2 text-xs font-normal cursor-help"
            data-testid={testid ? `customer-allocation-badge-${testid}` : 'customer-allocation-badge'}
          >
            <UserCircle2 className="h-3 w-3 mr-1" />
            {totalQty} un. para {clientCount === 1 ? 'cliente' : `${clientCount} clientes`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm bg-white border border-slate-200 shadow-lg">
          <div className="space-y-2">
            <p className="font-semibold text-sm text-slate-800">
              Alocações de cliente abertas
            </p>
            <ul className="text-xs text-slate-600 space-y-1.5">
              {Object.entries(byClient).map(([client, info]) => (
                <li key={client} className="border-l-2 border-indigo-200 pl-2">
                  <div className="font-medium text-slate-700">{client}</div>
                  <div className="text-slate-500">
                    {info.qty} un. ·{' '}
                    {info.sources
                      .map((s) =>
                        s.source === 'po'
                          ? `PO ${s.reference} (${s.status})`
                          : 'No carrinho'
                      )
                      .join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 pt-1 italic">
              Estas unidades já têm destino — confirma antes de encomendar mais.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CustomerAllocationBadge;
